import { useMutation } from '@tanstack/react-query';

import { apiClient } from '@/lib/api/client';
import { downloadBlob, filenameFromContentDisposition } from '@/lib/download';

export type ExportFormat = 'xlsx' | 'csv' | 'pdf';

/** UI token → backend wire enum. The backend's enum is `csv`/`pdf`/`excel`; the UI calls the
 *  Excel option `xlsx` (the extension of the file it returns). Single source so the token sent
 *  on the wire can't drift from the menu/extension. */
const WIRE_FORMAT: Record<ExportFormat, string> = {
  xlsx: 'excel',
  csv: 'csv',
  pdf: 'pdf',
};

const EXTENSION: Record<ExportFormat, string> = {
  xlsx: 'xlsx',
  csv: 'csv',
  pdf: 'pdf',
};

export type ExportParams = {
  format: ExportFormat;
  /** Fallback base name, without extension — used **only** where the response carries no
   *  `Content-Disposition`. See the note on `useExport`. */
  filename: string;
  /** Arbitrary backend filter params merged into the export query string (or JSON body when
   *  `method: 'post'`). */
  params?: Record<string, string | number | undefined>;
};

export type UseExportOptions = {
  /** `'get'` (default) sends filters as a query string; `'post'` sends them as a JSON body
   *  (the Requests-hub tabs' real `POST .../export`). */
  method?: 'get' | 'post';
};

/**
 * Downloads an export, **named by the server**.
 *
 * The name is the server's (`Content-Disposition`, RFC 6266): it is produced by the same code
 * that produced the bytes, so the two cannot drift. `filename` is a fallback for a response that
 * carries no header — not the normal path. Composing the name client-side instead is what
 * shipped six filename bugs at once (ADO 117594, 119639, 113010, 119211, 119231, 119292): the
 * API already emitted `Orders_20260921084110.xlsx` and `vrdasda_subscriptiondetails_…`, and
 * every caller threw it away for a date-only string of its own, four of them for a raw UUID.
 *
 * The header is only readable because the API sets `exposedHeaders: ['Content-Disposition']`;
 * cross-origin it is not CORS-safelisted. `filenameFromContentDisposition` strips path
 * separators, since the value reaches `a.download`.
 */
export function useExport(endpoint: string, { method = 'get' }: UseExportOptions = {}) {
  return useMutation({
    mutationFn: async ({ format, filename, params }: ExportParams) => {
      const body: Record<string, string | number> = { format: WIRE_FORMAT[format] };
      for (const [key, value] of Object.entries(params ?? {})) {
        if (value !== undefined && value !== '') body[key] = value;
      }
      const { blob, contentDisposition } =
        method === 'post'
          ? await apiClient.postFile(endpoint, body)
          : await apiClient.getFile(endpoint, { params: body });
      downloadBlob(
        blob,
        filenameFromContentDisposition(contentDisposition, `${filename}.${EXTENSION[format]}`),
      );
    },
  });
}
